import { useCallback, useMemo } from 'react';
import useSWR from 'swr';

import { backendClient } from '@/lib/api-client';

export const FEATURE_FLAGS = {
  DRAFTING: 'drafting',
  IMANAGE: 'imanage',
  KNOWLEDGE_GRAPH_UI: 'knowledge_graph_ui',
  NETDOCS: 'netdocs',
  ONEDRIVE: 'onedrive',
  SHAREPOINT: 'sharepoint',
  WEB_RESEARCH: 'web_research',
} as const;

export type FeatureId = typeof FEATURE_FLAGS[keyof typeof FEATURE_FLAGS];

export interface FeatureEntry {
  feature_id: FeatureId;
  enabled: boolean;
  settings?: Record<string, any> | null;
}

interface FeaturesResponse {
  features: FeatureEntry[];
}

export interface FeatureState {
  enabled: boolean;
  settings: Record<string, any>;
  options: string[];
}

export const FEATURES_CACHE_KEY = 'features_cache';
const fallback: FeaturesResponse = { features: [] };
const defaultFeatureState: FeatureState = { enabled: false, settings: {}, options: [] };

const fetcher = async (url: string): Promise<FeaturesResponse> => {
  const response = await backendClient.get(url);
  return response.data;
};

type UseFeaturesAllResult = {
  features: FeatureEntry[];
  featureMap: Record<string, FeatureEntry>;
  getFeature: (featureId: FeatureId) => FeatureState;
  isEnabled: (featureId: FeatureId) => boolean;
  isLoading: boolean;
  isValidating: boolean;
  error: any;
};

type UseFeatureByIdResult = FeatureState & {
  isEnabled: boolean;
  isLoading: boolean;
  isValidating: boolean;
  error: any;
};

export function useFeatures(featureId: FeatureId): UseFeatureByIdResult;
export function useFeatures(): UseFeaturesAllResult;
export function useFeatures(featureId?: FeatureId) {
  const cachedFallback = useMemo<FeaturesResponse>(() => {
    try {
      const raw = localStorage.getItem(FEATURES_CACHE_KEY);
      if (!raw) {
        return fallback;
      }
      const parsed = JSON.parse(raw) as FeaturesResponse;
      return Array.isArray(parsed?.features) ? parsed : fallback;
    } catch {
      return fallback;
    }
  }, []);

  const { data, isLoading, isValidating, error } = useSWR<FeaturesResponse>('/features', fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: 300000,
    fallbackData: cachedFallback,
    onSuccess: (response) => {
      if (typeof window === 'undefined') {
        return;
      }
      try {
        localStorage.setItem(FEATURES_CACHE_KEY, JSON.stringify(response));
      } catch {
        // Ignore storage failures (private mode/quota).
      }
    },
  });

  const features = useMemo(() => data?.features || [], [data]);
  const featureMap = useMemo(
    () =>
      features.reduce<Record<string, FeatureEntry>>((acc, feature) => {
        acc[feature.feature_id] = feature;
        return acc;
      }, {}),
    [features]
  );

  const getFeature = useCallback(
    (targetFeatureId: FeatureId): FeatureState => {
      const feature = featureMap[targetFeatureId];
      if (!feature) {
        return defaultFeatureState;
      }

      const settings = (feature.settings || {}) as Record<string, any>;
      const options = Array.isArray(settings.options)
        ? settings.options.filter((option): option is string => typeof option === 'string')
        : [];

      return {
        enabled: Boolean(feature.enabled),
        settings,
        options,
      };
    },
    [featureMap]
  );

  const isEnabled = useCallback((targetFeatureId: FeatureId): boolean => getFeature(targetFeatureId).enabled, [getFeature]);

  if (featureId) {
    const feature = getFeature(featureId);
    return {
      ...feature,
      isEnabled: feature.enabled,
      isLoading,
      isValidating,
      error,
    };
  }

  return { features, featureMap, getFeature, isEnabled, isLoading, isValidating, error };
};
